import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { X } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address').max(255),
});

export const NewsletterWidget = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = emailSchema.safeParse({ email: email.trim() });
    
    if (!validation.success) {
      toast({
        title: "Invalid email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        'https://xhbewsszcuxltgrrrscm.supabase.co/functions/v1/newsletter-subscribe',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: validation.data.email,
            source: 'affiliate_widget',
            metadata: {
              affiliate_id: 'affiliate-001',
              widget_position: 'floating',
              referrer: window.location.hostname,
            }
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Subscription failed');
      }

      toast({
        title: "Successfully subscribed!",
        description: "You'll receive the latest quantum computing insights.",
      });

      setEmail('');
      setTimeout(() => setIsVisible(false), 2000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-6 right-6 z-50 max-w-sm bg-card p-6 rounded-lg shadow-lg border border-border"
      aria-label="Newsletter subscription widget"
    >
      <button
        onClick={() => setIsVisible(false)}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Close newsletter widget"
      >
        <X className="h-4 w-4" />
      </button>

      <h3 className="text-lg font-semibold mb-2">Subscribe to our newsletter</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Get the latest quantum computing insights delivered to your inbox.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          required
        />
        <Button
          type="submit"
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </form>
    </div>
  );
};
